import React, { useContext, useEffect } from "react";

import sld from "../../images/sld/Rectangle.svg";

import { DesignAssetContext } from "../CustomHooks/ContextApi";
import Ecc from "../../components/GensetPvBess/Ecc";
import Busbar from "../../components/GensetPvBess/Busbar";
import Linebar from "../../components/GensetPvBess/Linebar";
import Utility from "../../components/GensetPvBess/Utility";
import Battery from "../../components/GensetPvBess/Battery";
import PV1 from "../../components/GensetPvBess/PV1";
import Genset from "../../components/GensetPvBess/Genset";

import PV2 from "../../components/GensetPvBess/PV2";

import Load from "../../components/GensetPvBess/Load";
import embLabels from "../../config/emb-labels-en.json";
import PVExsisting from "../../components/GensetPvBess/PVExsisting";
import BatteryExsisting from "../../components/GensetPvBess/BatteryExsisting";
import PV3 from "../../components/GensetPvBess/PV3";
import Ev from "../../components/ISLANDABLE/Ev";
import { uniqueId } from "lodash";
import RemoteLoad from "../../components/GensetPvBess/RemoteLoad";
import LoadNA from "../../components/GensetPvBess/LoadNA";
import LoadPvBess from "../../components/GensetPvBess/LoadPvBess";
import { initializeZoom } from "../../utils/zoomSLD";
import SldContainer from "../../components/SldContainer";
import GroundingTransformer from "../../components/ReusableSvg/GroundingTransformer";

// Constants
const SWITCHBOARD_TYPES = {
  EXISTING: 'EXISTING_SWITCHBOARD',
  NEW: 'NEW_SWITCHBOARD',
};

const LOAD_SIDES = {
  LOAD: 'LOAD_SIDE',
  LINE: 'LINE_SIDE',
};

const SITE_TYPES = {
  ISLANDABLE_GSX: 'ISLANDABLE_GSX',
  ISLANDABLE: 'ISLANDABLE',
};

const GensetPvBessSLD = () => {
  const {
    count,
    CBLoadList,
    CBAssetList,
    CBGTList,
    sldLoadCompleted,
    CBEVList,
    siteType,
    architectureType,
    switchboardType,
    loadSide,
    remoteMain,
    coordinates: { busbarCordinates },
  } = useContext(DesignAssetContext);
  let loadIndex = 0;
  let EccIndex = 0;
  const checkIndex = () => CBAssetList.findIndex(a => (a.assetType === "Inverter" && a.uiData.status === true));
  const areAllConfigured = () => {
    const loadList = CBLoadList.filter(load => !load.uiData.isRemoteLoad);
    return loadList.every(item => item.uiData.configured);
  }
  const isLoadSide = loadSide === LOAD_SIDES.LOAD;

  // Helper function to get component presence flags
  const getComponentFlags = () => ({
    isExisting: switchboardType === embLabels?.EXISTING_SWITCHBOARD,
    hasLoad: CBLoadList && CBLoadList.length > 0,
    hasEV: CBEVList && CBEVList.some((ev) => ev.uiData.status),
    hasGT: CBGTList && CBGTList.some((gt) => gt.uiData.status),
    derCount: count?.derCount || 0,
    evCount: count?.evCount || 0,
    gtCount: count?.gTCount || 0,
  });
  // Component positioning by DER count and switchboard type
  const POSITION_CONFIG = {
    EXISTING_SWITCHBOARD: {
      load: {
        base: { 6: 0, 5: 0, 4: 0, 3: -30, default: 0 },
        withGT: { 6: 30, 5: 80, 4: 50, 3: 40, default: 0 },
        withEV: { 6: 10, 5: 10, 4: 5, 3: 30, default: 0 },
        withEVandGT: { 6: 50, 5: 90, 4: 60, 3: 50, default: 0 },
      },
      gt: {
        base: { 6: 80, 5: 80, 4: 10, 3: 40, default: 0 },
        withLoad: { 6: 210, 5: 210, 4: 150, 3: 110, default: 0 },
      },
      ev: {
        base: { 6: 0, 5: 0, 4: 0, 3: 0, default: 0 },
        withLoad: { 6: 20, 5: 20, 4: 10, 3: 20, default: 0 },
        withGT: { 6: 80, 5: 80, 4: 50, 3: 60, default: 0 },
        withGTandLoad: { 6: 100, 5: 100, 4: 70, 3: 80, default: 0 },
      },
    },
    NEW_SWITCHBOARD: {
      load: {
        base: { 6: 5, 5: 5, 4: -20, 3: 10, default: 0 },
        withGT: { 6: 25, 5: 25, 4: 20, 3: 60, default: 0 },
        withEV: { 6: 15, 5: 15, 4: 0, 3: 40, default: 0 },
        withEVandGT: { 6: 45, 5: 45, 4: 30, 3: 70, default: 0 },
      },
      gt: {
        base: { 6: 180, 5: 185, 4: 105, 3: 30, default: 0 },
        withLoad: { 6: 335, 5: 285, 4: 230, 3: 190, default: 0 },
      },
      ev: {
        base: { 6: 0, 5: 0, 4: 0, 3: 0, default: 0 },
        withLoad: { 6: 30, 5: 30, 4: 15, 3: 30, default: 0 },
        withGT: { 6: 100, 5: 100, 4: 70, 3: 80, default: 0 },
        withGTandLoad: { 6: 130, 5: 130, 4: 100, 3: 110, default: 0 },
      },
    },
  };

  const getGTXPosition = () => {
    const { isExisting, hasLoad, hasEV, derCount } = getComponentFlags();

    // Get the appropriate config
    const config = isExisting
      ? POSITION_CONFIG.EXISTING_SWITCHBOARD.gt
      : POSITION_CONFIG.NEW_SWITCHBOARD.gt;

    // Scenario: Both Load and EV present
    if (hasLoad && hasEV && config.withEVandLoad) {
      return config.withEVandLoad[derCount] ?? config.withEVandLoad.default;
    }

    // Scenario: Only Load present (without EV)
    if (hasLoad && !hasEV && config.withLoad) {
      return config.withLoad[derCount] ?? config.withLoad.default;
    }

    // Scenario: No Load, no EV - use base position
    return config.base[derCount] ?? config.base.default;
  };

  const getEVXPosition = () => {
    const { isExisting, hasLoad, gtCount, derCount } = getComponentFlags();

    // Get the appropriate config based on switchboard type
    const config = isExisting
      ? POSITION_CONFIG.EXISTING_SWITCHBOARD.ev
      : POSITION_CONFIG.NEW_SWITCHBOARD.ev;

    // Scenario: Both GT and Load present
    if (gtCount >= 1 && hasLoad && config.withGTandLoad) {
      return config.withGTandLoad[derCount] ?? config.withGTandLoad.default;
    }

    // Scenario: GT present without Load
    if (gtCount >= 1 && config.withGT) {
      return config.withGT[derCount] ?? config.withGT.default;
    }

    // Scenario: Load present without GT
    if (hasLoad && config.withLoad) {
      return config.withLoad[derCount] ?? config.withLoad.default;
    }

    // Scenario: Neither GT nor Load present - use base position
    return config.base[derCount] ?? config.base.default;
  };

  const getLoadXPosition = () => {
    const { isExisting, derCount, hasEV, hasGT  } = getComponentFlags();

    // Get the appropriate config based on switchboard type
    const config = isExisting
      ? POSITION_CONFIG.EXISTING_SWITCHBOARD.load
      : POSITION_CONFIG.NEW_SWITCHBOARD.load;

    // Apply scenario-based positioning for BOTH switchboard types

    // Scenario 4: Both GT and EV present
    if (hasGT && hasEV && config.withEVandGT) {
      return config.withEVandGT[derCount] ?? config.withEVandGT.default;
    }

    // Scenario 2: EV without GT
    if (hasEV && !hasGT && config.withEV) {
      return config.withEV[derCount] ?? config.withEV.default;
    }

    // Scenario 3: GT without EV
    if (hasGT && !hasEV && config.withGT) {
      return config.withGT[derCount] ?? config.withGT.default;
    }

    // Scenario 1: Base position (no EV, no GT)
    return config.base[derCount] ?? config.base.default;
  };
  return (
    <SldContainer siteType={siteType}>
    <>


      {[SITE_TYPES.ISLANDABLE_GSX, SITE_TYPES.ISLANDABLE].includes(siteType) &&
        (switchboardType === "" ||
          switchboardType === embLabels.EXISTING_SWITCHBOARD) ? (
        <svg viewBox="0 0 1400 900" x="80" id="genset-pvbess-sld"
          className="sld-svg-container"
        >
          <g>
            <svg
              x={loadSide === LOAD_SIDES.LOAD ? busbarCordinates[`svgx`] : 0}
              y={loadSide === LOAD_SIDES.LOAD ? busbarCordinates[`svgy`] : 0}
              viewBox="0 0 1400 900"
            >
              {/* <g id="grid">
                <svg>
                  <image href={sld} x="0" y="0" />
                </svg>
              </g> */}

              <g>
                <svg viewBox="0 0 1400 900">
                  <g
                    id="components-sld"
                    style={sldLoadCompleted ? {} : { display: "none" }}
                  >
                    <Ecc />
                    <Busbar secondBusbar={true} />
                    <Linebar />
                    <svg x="0" y={"0"}>
                      <Utility />
                    </svg>
                    <svg x={count?.derCount ===3 ?"30":"0"} y={loadSide === LOAD_SIDES.LINE ? "0" : "50"}>
                      <Battery />
                      {CBAssetList[1].uiData.status && <Genset />}
                      {CBAssetList[3].uiData.status && <PV1 isFirst={checkIndex() === 3} />}
                      {CBAssetList[4].uiData.status && <PV2 isFirst={checkIndex() === 4} />}
                      {CBAssetList[5].uiData.status && <PV3 isFirst={checkIndex() === 5} />}
                      {CBAssetList[3].uiData.existingPmRequired && (
                        <PVExsisting />
                      )}
                      {CBAssetList[2].uiData.existingPmRequired && (
                        <BatteryExsisting />
                      )}

                      {switchboardType === "" ||
                        switchboardType === embLabels?.EXISTING_SWITCHBOARD ? (
                        <>
                          <svg className="pointer" x="0" y="0">
                            {CBLoadList.map((loadData, key) => {
                              loadData.uiData.isRemoteLoad && loadIndex++;
                              return (
                                loadData.uiData.isRemoteLoad && (
                                  <RemoteLoad
                                    key={uniqueId(loadData.configData.name)}
                                    load={loadData.uiData}
                                    loadName={loadData.configData.name}
                                    loadCbName={loadData.cbConfigData.name}
                                    index={key}
                                    loadIndex={loadIndex}
                                    remoteLoad={loadData.uiData.isRemoteLoad}
                                    powerMeterData={loadData.powerMeterData}
                                  />
                                )
                              );
                            })}
                          </svg>

                          {/* Load Component - Always Present */}
                          <svg x={getLoadXPosition()} y="0" id="load-container">
                            {CBLoadList.length > 0 &&
                              <>
                                <LoadPvBess
                                  loadLength={count}
                                  configured={areAllConfigured()}
                                />
                              </>
                            }
                          </svg>
                        </>
                      ) : (
                        <></>
                      )}
                      {/* EV Components - Conditional Rendering */}
                      {CBEVList.some((ev) => ev.uiData.status) && (
                        <svg x={getEVXPosition()} y="0" id="ev-container">
                          {CBEVList.map(
                            (cbData, key) =>
                              cbData?.uiData?.status && (
                                <Ev
                                  key={cbData?.configData?.name}
                                  index={key}
                                  status={cbData?.uiData?.status}
                                />
                              )
                          )}
                        </svg>
                      )}
                      {/* Grounding Transformer Components - Conditional Rendering */}
                      {CBGTList.some((gt) => gt.uiData.status) && (
                        <svg
                          x={getGTXPosition()}
                          y="0" id="gt-container">
                          {CBGTList.map(
                            (cbData, key) =>
                              cbData.uiData.status && (
                                <GroundingTransformer x="500" y={!isLoadSide ? "430" : "490"} key={cbData.configData.name} index={key} lineHieght={isLoadSide ? "91" : "105"} />
                              )
                          )}
                        </svg>
                      )}
                    </svg>
                  </g>
                </svg>
              </g>
            </svg>
          </g>
        </svg>
      ) : (
        <svg viewBox="0 0 1300 800" id="genset-pvbess-sld">
          <g>
            <svg x={count?.derCount === 3 ? 70 : 0} y="0" viewBox="0 0 1300 800">
              {/* <g id="grid">
                <svg>
                  <image href={sld} x="0" y="0" />
                </svg>
              </g> */}

              <g>
                <svg x="270" y="0" viewBox="0 0 1500 800">
                  <g
                    id="components-sld"
                    style={sldLoadCompleted ? {} : { display: "none" }}
                  >
                    <Ecc />
                    <Busbar secondBusbar={true} />
                    <svg x={count?.derCount === 3 ? 30 : count?.derCount === 4 ? 10 : 0} y="0">
                      <Utility />
                      <Battery />
                      {CBAssetList[1].uiData.status && <Genset />}
                      {CBAssetList[3].uiData.status && <PV1 />}
                      {CBAssetList[4].uiData.status && <PV2 />}
                      {CBAssetList[5].uiData.status && <PV3 />}
                    </svg>

                    {/* Load Component - Always Present */}
                    <svg x={getLoadXPosition()} y="0" id="load-container">
                      {CBLoadList.length > 0 &&
                        <LoadPvBess
                          loadLength={count}
                          configured={areAllConfigured()}
                        />
                      }
                    </svg>
                    {/* EV Components - Conditional Rendering */}
                    {CBEVList.some((ev) => ev.uiData.status) && (
                      <svg x={getEVXPosition()} y="0" id="ev-container">
                        {CBEVList.map(
                          (cbData, key) =>
                            cbData?.uiData?.status && (
                              <Ev
                                key={cbData?.configData?.name}
                                index={key}
                                status={cbData?.uiData?.status}
                              />
                            )
                        )}
                      </svg>
                    )}
                    {CBGTList.some((gt) => gt.uiData.status) && (
                      <svg
                        x={getGTXPosition()}
                        y="300" id="gt-container">
                        {CBGTList.map(
                          (cbData, key) =>
                            cbData.uiData.status && (
                              <GroundingTransformer x="0" y="0" key={cbData.configData.name} index={key} />
                            )
                        )}
                      </svg>
                    )}
                  </g>
                </svg>
              </g>
            </svg>
          </g>
        </svg>
      )}
</>
    </SldContainer>
  );
};

export default GensetPvBessSLD;
